/**
 * Train Results Component
 * Handles display and interaction with train search results
 */

import { TrainResult } from '../types/data-models';

export class TrainResultsComponent {
  private container: HTMLElement;
  private results: TrainResult[] = [];
  private loadingElement!: HTMLElement;
  private noResultsElement!: HTMLElement;
  private resultsListElement!: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;
    this.initializeElements();
  }

  private initializeElements(): void {
    this.loadingElement = this.container.querySelector('.train-results__loading') as HTMLElement;
    this.noResultsElement = this.container.querySelector('.train-results__no-results') as HTMLElement;
    this.resultsListElement = this.container.querySelector('.train-results__list') as HTMLElement;
  }

  public showLoading(): void {
    this.hideAllStates();
    if (this.loadingElement) {
      this.loadingElement.style.display = 'block';
    }
  }

  public showNoResults(): void {
    this.hideAllStates();
    if (this.noResultsElement) {
      this.noResultsElement.style.display = 'block';
    }
  }

  public showResults(results: TrainResult[]): void {
    this.results = results;
    this.hideAllStates();
    
    if (results.length === 0) {
      this.showNoResults();
      return;
    }

    if (this.resultsListElement) {
      this.resultsListElement.style.display = 'block';
      this.renderResults();
    }
  }

  private hideAllStates(): void {
    [this.loadingElement, this.noResultsElement, this.resultsListElement].forEach(element => {
      if (element) {
        element.style.display = 'none';
      }
    });
  }

  private renderResults(): void {
    if (!this.resultsListElement) return;

    this.resultsListElement.innerHTML = '';

    this.results.forEach(result => {
      const resultCard = this.createResultCard(result);
      this.resultsListElement.appendChild(resultCard);
    });
  }

  private createResultCard(result: TrainResult): HTMLElement {
    const card = document.createElement('div');
    card.className = 'train-result-card';
    card.setAttribute('data-train-id', result.id);

    const departureTime = this.formatTime(result.departureTime);
    const arrivalTime = this.formatTime(result.arrivalTime);
    const departureDate = this.formatDate(result.departureTime);
    const stopsText = result.stops.length > 0 ? `${result.stops.length} arrêt(s)` : 'Direct';

    card.innerHTML = `
      <div class="train-result-card__header">
        <div class="train-result-card__operator">
          <img 
            src="${result.operatorLogo}" 
            alt="${result.operator}" 
            class="train-result-card__operator-logo"
            loading="lazy"
          >
          <div class="train-result-card__operator-info">
            <span class="train-result-card__operator-name">${result.operator}</span>
            <span class="train-result-card__train-number">${result.trainNumber}</span>
          </div>
        </div>
        <div class="train-result-card__price">
          <span class="train-result-card__price-amount">€${result.price.min}</span>
          <span class="train-result-card__price-currency">par personne</span>
        </div>
      </div>

      <div class="train-result-card__journey">
        <div class="train-result-card__route">
          <div class="train-result-card__station train-result-card__station--departure">
            <div class="train-result-card__time">${departureTime}</div>
            <div class="train-result-card__station-name">${result.origin}</div>
            <div class="train-result-card__date">${departureDate}</div>
            ${result.platform ? `<div class="train-result-card__platform">Voie ${result.platform}</div>` : ''}
          </div>

          <div class="train-result-card__journey-info">
            <div class="train-result-card__duration">${result.duration}</div>
            <div class="train-result-card__route-line">
              <div class="train-result-card__route-line-track"></div>
              <div class="train-result-card__route-line-train">🚄</div>
            </div>
            <div class="train-result-card__stops">${stopsText}</div>
          </div>

          <div class="train-result-card__station train-result-card__station--arrival">
            <div class="train-result-card__time">${arrivalTime}</div>
            <div class="train-result-card__station-name">${result.destination}</div>
            <div class="train-result-card__date">${this.formatDate(result.arrivalTime)}</div>
          </div>
        </div>
      </div>

      <div class="train-result-card__details">
        <div class="train-result-card__class">
          <span class="train-result-card__class-label">Classe:</span>
          <span class="train-result-card__class-value">${result.class}</span>
        </div>
        
        ${result.amenities.length > 0 ? `
          <div class="train-result-card__amenities">
            <span class="train-result-card__amenities-label">Services:</span>
            <div class="train-result-card__amenities-list">
              ${result.amenities.map(amenity => `
                <span class="train-result-card__amenity">${amenity}</span>
              `).join('')}
            </div>
          </div>
        ` : ''}

        ${result.seatTypes.length > 0 ? `
          <div class="train-result-card__seat-types">
            <span class="train-result-card__seat-types-label">Types de sièges:</span>
            <div class="train-result-card__seat-types-list">
              ${result.seatTypes.map(seatType => `
                <span class="train-result-card__seat-type">${seatType}</span>
              `).join('')}
            </div>
          </div>
        ` : ''}
      </div>

      ${result.stops.length > 0 ? `
        <div class="train-result-card__stops-detail" style="display: none;">
          <h4 class="train-result-card__stops-title">Arrêts intermédiaires:</h4>
          <ul class="train-result-card__stops-list">
            ${result.stops.map(stop => `
              <li class="train-result-card__stop">${stop}</li>
            `).join('')}
          </ul>
        </div>
      ` : ''}

      <div class="train-result-card__actions">
        ${result.stops.length > 0 ? `
          <button 
            type="button" 
            class="btn btn--secondary train-result-card__toggle-stops"
            aria-expanded="false"
          >
            Voir les arrêts
          </button>
        ` : ''}
        <button type="button" class="btn btn--primary train-result-card__select">
          Sélectionner ce train
        </button>
      </div>
    `;

    this.bindCardEvents(card, result);
    return card;
  }

  private bindCardEvents(card: HTMLElement, result: TrainResult): void {
    // Toggle stops detail
    const toggleStopsBtn = card.querySelector('.train-result-card__toggle-stops') as HTMLButtonElement;
    const stopsDetail = card.querySelector('.train-result-card__stops-detail') as HTMLElement;
    
    if (toggleStopsBtn && stopsDetail) {
      toggleStopsBtn.addEventListener('click', () => {
        const isExpanded = toggleStopsBtn.getAttribute('aria-expanded') === 'true';
        
        if (isExpanded) {
          stopsDetail.style.display = 'none';
          toggleStopsBtn.setAttribute('aria-expanded', 'false');
          toggleStopsBtn.textContent = 'Voir les arrêts';
        } else {
          stopsDetail.style.display = 'block';
          toggleStopsBtn.setAttribute('aria-expanded', 'true');
          toggleStopsBtn.textContent = 'Masquer les arrêts';
        }
      });
    }

    // Select train button
    const selectBtn = card.querySelector('.train-result-card__select') as HTMLButtonElement;
    if (selectBtn) {
      selectBtn.addEventListener('click', () => {
        this.handleTrainSelection(result);
      });
    }
  }

  private handleTrainSelection(result: TrainResult): void {
    // Emit custom event for train selection
    const event = new CustomEvent('trainSelected', {
      detail: { train: result },
      bubbles: true
    });
    this.container.dispatchEvent(event);
  }

  private formatTime(date: Date): string {
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit'
    });
  }

  public getResults(): TrainResult[] {
    return [...this.results];
  }

  public clear(): void {
    this.results = [];
    if (this.resultsListElement) {
      this.resultsListElement.innerHTML = '';
    }
    this.hideAllStates();
  }
}